/**
 * components/branding/ClientBrandingHeader.tsx
 *
 * Unified branding component used on every public client-facing screen.
 * Shows the sender's logo (or a monogram fallback) and optional display name.
 *
 * Sizes:
 *   sm  - compact horizontal row for SectionTransition cards (logo h-8 + name)
 *   md  - standard horizontal row for page headers and error screens (logo h-12 + name)
 *   lg  - large centered stack for the WelcomeScreen (logo h-20/24 above name)
 *
 * Rule: every public client screen MUST include this component so the sender's
 * identity is always visible.
 *
 * Created Apr 2026: part of the continuous branding refactor for the
 *   client-facing form experience.
 * Updated Apr 2026: initials now fall back to displayName-derived letters when
 *   firstName/lastName are absent, so callers that only pass displayName get
 *   meaningful monogram letters instead of the generic User icon.
 */

import BrandMonogram from './BrandMonogram';
import { getSenderInitials } from '../../utils/brandingHelpers';

interface ClientBrandingHeaderProps {
  logoUrl: string | null;
  displayName: string | null;
  /** First name for monogram initials when logoUrl is null */
  firstName?: string | null;
  /** Last name for monogram initials when logoUrl is null */
  lastName?: string | null;
  size: 'sm' | 'md' | 'lg';
}

const LOGO_CLASSES: Record<ClientBrandingHeaderProps['size'], string> = {
  sm: 'h-8 w-auto object-contain',
  md: 'h-12 md:h-14 w-auto object-contain',
  lg: 'h-20 md:h-24 w-auto object-contain',
};

const NAME_CLASSES: Record<ClientBrandingHeaderProps['size'], string> = {
  sm: 'text-sm font-medium text-neutral-700',
  md: 'text-sm font-semibold text-neutral-800',
  lg: 'text-base font-semibold text-neutral-700 mt-3',
};

function getInitials(firstName: string | null | undefined, lastName: string | null | undefined, displayName: string | null): string | null {
  const fromNames = getSenderInitials({ first_name: firstName, last_name: lastName });
  if (fromNames) return fromNames;
  if (!displayName) return null;
  const words = displayName.trim().split(/\s+/).filter(Boolean);
  const letters = words.slice(0, 2).map(w => w[0].toUpperCase()).join('');
  return letters || null;
}

export default function ClientBrandingHeader({
  logoUrl,
  displayName,
  firstName,
  lastName,
  size,
}: ClientBrandingHeaderProps) {
  const initials = getInitials(firstName, lastName, displayName);

  if (size === 'lg') {
    return (
      <div className="flex flex-col items-center">
        {logoUrl ? (
          <img
            src={logoUrl}
            alt={displayName ? `Logo de ${displayName}` : 'Logo'}
            className={LOGO_CLASSES.lg}
          />
        ) : (
          <BrandMonogram initials={initials} size="lg" />
        )}
        {displayName && (
          <p className={NAME_CLASSES.lg}>{displayName}</p>
        )}
      </div>
    );
  }

  return (
    <div className="flex items-center gap-3">
      {logoUrl ? (
        <img
          src={logoUrl}
          alt={displayName ? `Logo de ${displayName}` : 'Logo'}
          className={LOGO_CLASSES[size]}
        />
      ) : (
        <BrandMonogram initials={initials} size={size === 'sm' ? 'sm' : 'md'} />
      )}
      {displayName && (
        <span className={NAME_CLASSES[size]}>{displayName}</span>
      )}
    </div>
  );
}
